import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WorkingProfile, PolicyViolationSettings, PolicyLimits, PolicyCheckType } from '@/types/timesheetSettings';
import { HelpCircle } from 'lucide-react';

interface WorkingHoursConfigurationCompactProps {
  workingProfile: WorkingProfile;
  policyEnforcement: PolicyViolationSettings;
  policyLimits: PolicyLimits | undefined;
  onWorkingProfileChange: (profile: WorkingProfile) => void;
  onPolicyEnforcementChange: (enforcement: PolicyViolationSettings) => void;
  onPolicyLimitsChange: (limits: PolicyLimits | undefined) => void;
}

export function WorkingHoursConfigurationCompact({
  workingProfile,
  policyEnforcement,
  policyLimits,
  onWorkingProfileChange,
  onPolicyEnforcementChange,
  onPolicyLimitsChange,
}: WorkingHoursConfigurationCompactProps) {
  const [weeklyHoursInput, setWeeklyHoursInput] = React.useState<string>(workingProfile.standardHoursPerWeek.toFixed(2));
  const [dailyHoursInputs, setDailyHoursInputs] = React.useState<Record<string, string>>({});

  // Update local input when workingProfile changes externally
  React.useEffect(() => {
    setWeeklyHoursInput(workingProfile.standardHoursPerWeek.toFixed(2));
  }, [workingProfile.standardHoursPerWeek]);

  // Initialize daily hours inputs
  React.useEffect(() => {
    const inputs: Record<string, string> = {};
    Object.entries(workingProfile.workingDays).forEach(([key, dayData]) => {
      inputs[key] = dayData.expectedHours.toFixed(2);
    });
    setDailyHoursInputs(inputs);
  }, [workingProfile.workingDays]);

  // Calculate working days and daily hours
  const workingDays = Object.values(workingProfile.workingDays).filter(d => d.isWorkingDay);
  const avgDailyHours = workingDays.length > 0 
    ? workingProfile.standardHoursPerWeek / workingDays.length 
    : 8;

  // Quick preset handler (hours only)
  const applyQuickHours = (hours: number) => {
    // Get current working days
    const currentWorkingDays = Object.entries(workingProfile.workingDays)
      .filter(([_, dayData]) => dayData.isWorkingDay);
    
    if (currentWorkingDays.length === 0) {
      // If no working days, just update the weekly hours
      onWorkingProfileChange({
        ...workingProfile,
        standardHoursPerWeek: hours,
      });
      return;
    }

    // Calculate new daily hours
    const newDailyHours = hours / currentWorkingDays.length;
    
    // Update working days with new hours (only for non-custom days)
    const updatedWorkingDays = { ...workingProfile.workingDays };
    currentWorkingDays.forEach(([dayKey, dayData]) => {
      if (!dayData.isCustom) {
        updatedWorkingDays[dayKey as keyof typeof workingProfile.workingDays] = {
          ...dayData,
          expectedHours: newDailyHours,
        };
      }
    });

    onWorkingProfileChange({
      ...workingProfile,
      standardHoursPerWeek: hours,
      workingDays: updatedWorkingDays,
    });
  };

  // Quick preset handler with days
  const applyPreset = (hours: number, days: string[]) => {
    const dailyHours = hours / days.length;
    const workingDaysConfig: typeof workingProfile.workingDays = {
      monday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      tuesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      wednesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      thursday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      friday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      saturday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      sunday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
    };

    days.forEach((day) => {
      workingDaysConfig[day as keyof typeof workingDaysConfig] = {
        isWorkingDay: true,
        expectedHours: dailyHours,
        isCustom: false,
      };
    });

    onWorkingProfileChange({
      standardHoursPerWeek: hours,
      workingDays: workingDaysConfig,
    });
  };

  // Policy check handlers
  const toggleCheck = (checkType: PolicyCheckType, enabled: boolean) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          enabled,
        },
      },
      preset: 'custom',
    });
  };

  const updateThreshold = (checkType: PolicyCheckType, threshold: number) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          threshold,
        },
      },
      preset: 'custom',
    });
  };

  const updateSeverity = (checkType: PolicyCheckType, severity: 'info' | 'warning' | 'critical') => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          severity,
        },
      },
      preset: 'custom',
    });
  };

  // Legal limits handlers
  const toggleLegalLimits = (enabled: boolean) => {
    if (enabled) {
      onPolicyLimitsChange({
        legalMaxHoursPerDay: 13,
        legalMaxHoursPerWeek: 48,
      });
    } else {
      onPolicyLimitsChange(undefined);
    }
  };

  const updateLegalLimit = (field: 'legalMaxHoursPerDay' | 'legalMaxHoursPerWeek', value: number) => {
    if (policyLimits) {
      onPolicyLimitsChange({
        ...policyLimits,
        [field]: value,
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header - Minimal */}
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-xl font-semibold">Working Hours Configuration</h2>
        </div>

        {/* Standard Hours - Unified Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Standard Hours</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Your organization's baseline working schedule. All policy checks use these hours as reference.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            {/* Quick Presets - Inline */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Presets:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(20, ['monday', 'tuesday', 'wednesday'])}
                className="h-7 px-2 text-xs"
              >
                20h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(30, ['monday', 'tuesday', 'wednesday', 'thursday'])}
                className="h-7 px-2 text-xs"
              >
                30h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(37.5, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
                className="h-7 px-2 text-xs"
              >
                37.5h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(40, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
                className="h-7 px-2 text-xs"
              >
                40h
              </Button>
            </div>
          </div>

          {/* Combined Weekly Hours and Days Grid */}
          <div className="p-4 bg-muted/20 rounded-lg border space-y-4">
            {/* Weekly Hours Input */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground min-w-[60px]">Total:</Label>
              <Input
                type="number"
                value={weeklyHoursInput}
                onChange={(e) => {
                  setWeeklyHoursInput(e.target.value);
                }}
                onBlur={(e) => {
                  // Apply changes on blur
                  const value = parseFloat(e.target.value) || 0;
                  const clampedValue = Math.max(0, Math.min(168, value));
                  applyQuickHours(clampedValue);
                }}
                onKeyDown={(e) => {
                  // Apply changes on Enter key
                  if (e.key === 'Enter') {
                    const value = parseFloat(weeklyHoursInput) || 0;
                    const clampedValue = Math.max(0, Math.min(168, value));
                    applyQuickHours(clampedValue);
                    e.currentTarget.blur();
                  }
                }}
                className="w-24 h-8"
                min="0"
                step="0.01"
              />
              <span className="text-xs text-muted-foreground">h/week</span>
            </div>

            {/* Days and Hours Grid */}
            <div className="grid grid-cols-7 gap-3">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index] as keyof typeof workingProfile.workingDays;
                const dayData = workingProfile.workingDays[dayKey];
                
                return (
                  <div key={day} className="space-y-2">
                    <Label className="text-xs font-medium text-center block">{day}</Label>
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={dayData.isWorkingDay}
                        onCheckedChange={(checked) => {
                          const updatedWorkingDays = {
                            ...workingProfile.workingDays,
                            [dayKey]: {
                              ...dayData,
                              isWorkingDay: checked,
                              expectedHours: checked ? (dayData.expectedHours || avgDailyHours) : 0,
                            },
                          };
                          
                          // Recalculate weekly total from all days
                          const newWeeklyTotal = Object.values(updatedWorkingDays)
                            .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                          
                          onWorkingProfileChange({
                            ...workingProfile,
                            standardHoursPerWeek: newWeeklyTotal,
                            workingDays: updatedWorkingDays,
                          });
                        }}
                        className="scale-90"
                      />
                    </div>
                    <Input
                      type="number"
                      value={dailyHoursInputs[dayKey] || '0.00'}
                      onChange={(e) => {
                        setDailyHoursInputs({
                          ...dailyHoursInputs,
                          [dayKey]: e.target.value,
                        });
                      }}
                      onBlur={(e) => {
                        // Apply changes on blur
                        const value = parseFloat(e.target.value) || 0;
                        const clampedValue = Math.max(0, Math.min(24, value));
                        
                        const updatedWorkingDays = {
                          ...workingProfile.workingDays,
                          [dayKey]: {
                            ...dayData,
                            expectedHours: clampedValue,
                            isCustom: true,
                          },
                        };
                        
                        // Recalculate weekly total from all days
                        const newWeeklyTotal = Object.values(updatedWorkingDays)
                          .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                        
                        onWorkingProfileChange({
                          ...workingProfile,
                          standardHoursPerWeek: newWeeklyTotal,
                          workingDays: updatedWorkingDays,
                        });
                      }}
                      onKeyDown={(e) => {
                        // Apply changes on Enter key
                        if (e.key === 'Enter') {
                          const value = parseFloat(dailyHoursInputs[dayKey]) || 0;
                          const clampedValue = Math.max(0, Math.min(24, value));
                          
                          const updatedWorkingDays = {
                            ...workingProfile.workingDays,
                            [dayKey]: {
                              ...dayData,
                              expectedHours: clampedValue,
                              isCustom: true,
                            },
                          };
                          
                          const newWeeklyTotal = Object.values(updatedWorkingDays)
                            .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                          
                          onWorkingProfileChange({
                            ...workingProfile,
                            standardHoursPerWeek: newWeeklyTotal,
                            workingDays: updatedWorkingDays,
                          });
                          
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={!dayData.isWorkingDay}
                      className="h-9 text-sm text-center"
                      min="0"
                      step="0.01"
                    />
                    <div className="text-xs text-center text-muted-foreground">
                      {dayData.isWorkingDay ? `${dayData.expectedHours.toFixed(2)}h` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Policy Thresholds - Compact Inline */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Policy Thresholds</Label>
            <span className="text-xs text-muted-foreground">(Optional)</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Show warnings when hours exceed thresholds. Calculated based on standard hours above.</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="space-y-2">
            {/* Daily Overage */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
              <Switch
                checked={policyEnforcement.checks['daily-overage']?.enabled || false}
                onCheckedChange={(checked) => toggleCheck('daily-overage', checked)}
                className="scale-90"
              />
              <Label className="text-sm flex-shrink-0 w-32">Daily overage</Label>
              <Select
                value={String(policyEnforcement.checks['daily-overage']?.threshold || 1.25)}
                onValueChange={(value) => updateThreshold('daily-overage', parseFloat(value))}
                disabled={!policyEnforcement.checks['daily-overage']?.enabled}
              >
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="1.1">110%</SelectItem>
                  <SelectItem value="1.15">115%</SelectItem>
                  <SelectItem value="1.2">120%</SelectItem>
                  <SelectItem value="1.25">125%</SelectItem>
                  <SelectItem value="1.3">130%</SelectItem>
                  <SelectItem value="1.5">150%</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                ({(avgDailyHours * (policyEnforcement.checks['daily-overage']?.threshold || 1.25)).toFixed(2)}h)
              </span>
              <div className="flex-1" />
              <Select
                value={policyEnforcement.checks['daily-overage']?.severity || 'warning'}
                onValueChange={(value: any) => updateSeverity('daily-overage', value)}
                disabled={!policyEnforcement.checks['daily-overage']?.enabled}
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="info">ℹ️ Info</SelectItem>
                  <SelectItem value="warning">⚠️ Warning</SelectItem>
                  <SelectItem value="critical">🚫 Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Weekly Overage */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
              <Switch
                checked={policyEnforcement.checks['weekly-overage']?.enabled || false}
                onCheckedChange={(checked) => toggleCheck('weekly-overage', checked)}
                className="scale-90"
              />
              <Label className="text-sm flex-shrink-0 w-32">Weekly overage</Label>
              <Select
                value={String(policyEnforcement.checks['weekly-overage']?.threshold || 1.1)}
                onValueChange={(value) => updateThreshold('weekly-overage', parseFloat(value))}
                disabled={!policyEnforcement.checks['weekly-overage']?.enabled}
              >
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="1.05">105%</SelectItem>
                  <SelectItem value="1.1">110%</SelectItem>
                  <SelectItem value="1.15">115%</SelectItem>
                  <SelectItem value="1.2">120%</SelectItem>
                  <SelectItem value="1.25">125%</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                ({(workingProfile.standardHoursPerWeek * (policyEnforcement.checks['weekly-overage']?.threshold || 1.1)).toFixed(2)}h)
              </span>
              <div className="flex-1" />
              <Select
                value={policyEnforcement.checks['weekly-overage']?.severity || 'warning'}
                onValueChange={(value: any) => updateSeverity('weekly-overage', value)}
                disabled={!policyEnforcement.checks['weekly-overage']?.enabled}
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="info">ℹ️ Info</SelectItem>
                  <SelectItem value="warning">⚠️ Warning</SelectItem>
                  <SelectItem value="critical">🚫 Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Missing Hours */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
              <Switch
                checked={policyEnforcement.checks['missing-hours']?.enabled || false}
                onCheckedChange={(checked) => toggleCheck('missing-hours', checked)}
                className="scale-90"
              />
              <Label className="text-sm flex-shrink-0 w-32">Missing hours</Label>
              <Select
                value={String(policyEnforcement.checks['missing-hours']?.threshold || 0.8)}
                onValueChange={(value) => updateThreshold('missing-hours', parseFloat(value))}
                disabled={!policyEnforcement.checks['missing-hours']?.enabled}
              >
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="0.9">90%</SelectItem>
                  <SelectItem value="0.85">85%</SelectItem>
                  <SelectItem value="0.8">80%</SelectItem>
                  <SelectItem value="0.75">75%</SelectItem>
                  <SelectItem value="0.7">70%</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                ({(workingProfile.standardHoursPerWeek * (policyEnforcement.checks['missing-hours']?.threshold || 0.8)).toFixed(2)}h)
              </span>
              <div className="flex-1" />
              <Select
                value={policyEnforcement.checks['missing-hours']?.severity || 'info'}
                onValueChange={(value: any) => updateSeverity('missing-hours', value)}
                disabled={!policyEnforcement.checks['missing-hours']?.enabled}
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="info">ℹ️ Info</SelectItem>
                  <SelectItem value="warning">⚠️ Warning</SelectItem>
                  <SelectItem value="critical">🚫 Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Legal Limits - Compact Inline */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Legal Limits</Label>
            <span className="text-xs text-muted-foreground">(Optional)</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Absolute maximums that block timesheet submission. Independent of policy thresholds.</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg border">
            <Switch
              checked={!!policyLimits}
              onCheckedChange={toggleLegalLimits}
            />
            <Label className="text-sm">Enable hard limits</Label>

            {policyLimits && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={policyLimits.legalMaxHoursPerDay}
                    onChange={(e) => updateLegalLimit('legalMaxHoursPerDay', parseFloat(e.target.value) || 0)}
                    className="w-16 h-8 text-xs"
                    min="0"
                    max="24"
                    step="0.5"
                  />
                  <span className="text-xs text-muted-foreground">h/day max</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={policyLimits.legalMaxHoursPerWeek}
                    onChange={(e) => updateLegalLimit('legalMaxHoursPerWeek', parseFloat(e.target.value) || 0)}
                    className="w-16 h-8 text-xs"
                    min="0"
                    max="168"
                    step="0.5"
                  />
                  <span className="text-xs text-muted-foreground">h/week max</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
